import { apiFetch, apiBaseUrl } from './apiClient';
import type { ILottery } from 'types/ILottery';
import type { IDrawing } from 'types/IDrawing';
import type { IDrawingSummary } from 'types/IDrawingSummary';
import type { IDrawingBase } from 'types/IDrawingBase';
import type { IPrizeCount, IPrizeCountInput } from 'types/IPrizeCount';
import type { IDrawingPrize, IDrawingPrizeDigits } from 'types/IDrawingPrize';
import type { IPrize, IPrizeUpload } from 'types/IPrize';
import type { IDrawingTicket } from 'types/IDrawingTicket';
import type { IUser } from 'types/IUser';
import type { IWorkflowHistory } from 'types/IWorkflow';

// Auth
export const apiLogin = (username: string, password: string) =>
  apiFetch<{ message: string; user: { id: number; username: string; email: string } }>('/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

export const apiLogout = () => apiFetch<{ message: string }>('/auth/logout', { method: 'POST' });

export const apiCurrentUser = () => apiFetch<{ user: { id: number; username: string; email: string } }>('/auth/me', { method: 'POST' });

// Lotteries
export const apiGetLotteries = () => apiFetch<ILottery[]>('/lottery');
export const apiGetLottery = (id: number) => apiFetch<ILottery>(`/lottery/${id}`);
export const apiGetDrawingsVisible = (lotteryId: number) => apiFetch<{ drawingsVisible: number }>(`/lottery/${lotteryId}/drawings-visible`);
export const apiSetDrawingsVisible = (lotteryId: number, drawingsVisible: number) =>
  apiFetch<{ drawingsVisible: number }>(`/lottery/${lotteryId}/drawings-visible`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ drawingsVisible }),
  });

// Drawings
export const apiGetDrawings = (drawingBaseId?: number) => apiFetch<IDrawing[]>(drawingBaseId ? `/drawing?drawingBaseId=${drawingBaseId}` : '/drawing');
export const apiGetDrawing = (id: number) => apiFetch<IDrawing>(`/drawing/${id}`);
export const apiCreateDrawing = (data: Partial<IDrawing>) =>
  apiFetch<IDrawing>('/drawing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiUpdateDrawing = (id: number, data: Partial<IDrawing>) =>
  apiFetch<IDrawing>(`/drawing/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiDeleteDrawing = (id: number) => apiFetch<void>(`/drawing/${id}`, { method: 'DELETE' });
export const apiGetDrawingSummary = (id: number) => apiFetch<IDrawingSummary>(`/drawing/${id}/summary`);
export const apiGetDrawingPrizes = (drawingId: number) => apiFetch<IDrawingPrize[]>(`/drawing/${drawingId}/prizes`);
export const apiAddDrawingPrize = (drawingId: number, prizeId: number, digits?: string) =>
  apiFetch<IDrawingPrize>(`/drawing/${drawingId}/prizes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prizeId, digits: digits || undefined }),
  });
export const apiUpdateDrawingPrizes = (drawingId: number, prizes: IDrawingPrizeDigits[]) =>
  apiFetch<IDrawingPrize[]>(`/drawing/${drawingId}/prizes`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prizes }),
  });
export const apiDeleteDrawingPrize = (drawingId: number, drawingPrizeId: number) =>
  apiFetch<void>(`/drawing/${drawingId}/prizes/${drawingPrizeId}`, { method: 'DELETE' });

export const apiGetPrizeCounts = (drawingId: number) => apiFetch<IPrizeCount[]>(`/drawing/${drawingId}/prize-counts`);
export const apiUpdatePrizeCounts = (drawingId: number, counts: IPrizeCountInput[]) =>
  apiFetch<IPrizeCount[]>(`/drawing/${drawingId}/prize-counts`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ counts }),
  });

// Drawing Bases
export const apiGetDrawingBases = (lotteryId?: number) => apiFetch<IDrawingBase[]>(lotteryId ? `/drawing-base?lotteryId=${lotteryId}` : '/drawing-base');
export const apiGetDrawingBase = (id: number) => apiFetch<IDrawingBase>(`/drawing-base/${id}`);
export const apiCreateDrawingBase = (data: Partial<IDrawingBase>) =>
  apiFetch<IDrawingBase>('/drawing-base', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiUpdateDrawingBase = (id: number, data: Partial<IDrawingBase>) =>
  apiFetch<IDrawingBase>(`/drawing-base/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiDeleteDrawingBase = (id: number) => apiFetch<void>(`/drawing-base/${id}`, { method: 'DELETE' });
export const apiCheckDrawingBaseHasDrawings = (id: number) => apiFetch<{ hasDrawings: boolean }>(`/drawing-base/${id}/has-drawings`);

// Prizes
export const apiGetPrizes = (drawingBaseId?: number) => apiFetch<IPrize[]>(drawingBaseId ? `/prize?drawingBaseId=${drawingBaseId}` : '/prize');
export const apiGetPrize = (id: number) => apiFetch<IPrize>(`/prize/${id}`);
export const apiCreatePrize = (data: Partial<IPrize>) =>
  apiFetch<IPrize>('/prize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiUpdatePrize = (id: number, data: Partial<IPrize>) =>
  apiFetch<IPrize>(`/prize/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiDeletePrize = (id: number) => apiFetch<void>(`/prize/${id}`, { method: 'DELETE' });

export const apiGetPrizeUploads = (prizeId: number) => apiFetch<IPrizeUpload[]>(`/prize/${prizeId}/uploads`);
export const apiUploadPrizeFile = (prizeId: number, file: File) => {
  const body = new FormData();
  body.append('prize_file', file);
  // No Content-Type header: the browser has to set the multipart boundary itself.
  return apiFetch<IPrizeUpload>(`/prize/${prizeId}/upload`, { method: 'POST', body });
};
export const apiDeletePrizeUpload = (prizeId: number, uploadId: number) =>
  apiFetch<void>(`/prize/${prizeId}/upload/${uploadId}`, { method: 'DELETE' });
/** Opened in a tab rather than fetched, so the link carries the API base itself. */
export const prizeFileUrl = (uploadId: number) => `${apiBaseUrl}/files/prize/${uploadId}`;

export const apiGetDrawingTickets = (drawingId: number) => apiFetch<IDrawingTicket[]>(`/drawing/${drawingId}/tickets`);
export const apiUploadDrawingTickets = (drawingId: number, file: File) => {
  const body = new FormData();
  body.append('ticket_file', file);
  // No Content-Type header: the browser has to set the multipart boundary itself.
  return apiFetch<{ count: number }>(`/drawing/${drawingId}/tickets/upload`, { method: 'POST', body });
};

// Workflow
export const apiSubmitDrawing = (drawingId: number, comment?: string) =>
  apiFetch<void>(`/drawing/${drawingId}/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
export const apiApproveDrawing = (drawingId: number, comment?: string) =>
  apiFetch<void>(`/drawing/${drawingId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
export const apiRejectDrawing = (drawingId: number, reason: string) =>
  apiFetch<void>(`/drawing/${drawingId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
export const apiGetWorkflowHistory = (drawingId: number) => apiFetch<IWorkflowHistory[]>(`/workflow/history/${drawingId}`);

// Users
export const apiGetUsers = () => apiFetch<IUser[]>('/users');
export const apiGetUsersByLottery = (lotteryId: number) => apiFetch<IUser[]>(`/users/lottery/${lotteryId}`);
export const apiGetUser = (id: number) => apiFetch<IUser>(`/users/${id}`);
export const apiCreateUser = (data: Partial<IUser> & { password: string }) =>
  apiFetch<IUser>('/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiUpdateUser = (id: number, data: Partial<IUser>) =>
  apiFetch<IUser>(`/users/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
export const apiDeleteUser = (id: number) => apiFetch<void>(`/users/${id}`, { method: 'DELETE' });
export const apiChangePassword = (oldPassword: string, newPassword: string) =>
  apiFetch<void>('/users/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ oldPassword, newPassword }),
  });
export const apiResetPassword = (userId: number, newPassword: string) =>
  apiFetch<void>(`/users/${userId}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ newPassword }),
  });
export const apiActivateUser = (userId: number) => apiFetch<void>(`/users/${userId}/activate`, { method: 'POST' });
export const apiDeactivateUser = (userId: number) => apiFetch<void>(`/users/${userId}/deactivate`, { method: 'POST' });

// Statistics
export interface StatEntry { year: number; month: number; count: number; }
export interface BranchStats { branchCode: number; branchName: string; overviewHits: StatEntry[]; queryHits: StatEntry[]; }
export interface LotteryStats { lotteryId: number; overviewHits: StatEntry[]; queryHits: StatEntry[]; byBranch: BranchStats[]; }
export const apiGetStatsByLottery = (lotteryId: number) => apiFetch<LotteryStats>(`/statistics/lottery/${lotteryId}`);

// Branches
export const apiGetBranches = (lotteryId?: number) => apiFetch<unknown[]>(lotteryId ? `/branch?lotteryId=${lotteryId}` : '/branch');
